import React, { useState, useEffect, useRef } from "react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import apiService from "../../services/ApiService";
import { NetworkError, ApiError } from "../../types/api";

interface Shift {
  id: string;
  pic_name: string;
  device_id: string;
  status: "active" | "locked" | "done";
  start_time: string;
  end_time?: string;
  created_at: string;
}

export const ShiftsPanel: React.FC = () => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState<"active" | "locked" | "done" | "">(
    ""
  );
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "locked" | "done"
  >("locked");

  // NEW: Add polling state and last update tracking
  const [isPolling, setIsPolling] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const POLLING_INTERVAL = 10000; // 10 seconds

  // NEW: Add state for current time to trigger re-renders for time display
  const [currentTime, setCurrentTime] = useState(new Date());

  // NEW: Use ref to track current status filter for polling
  const currentStatusFilter = useRef<"all" | "active" | "locked" | "done">(
    "locked"
  );

  useEffect(() => {
    loadShifts();
    startPolling();

    // NEW: Update current time every second for accurate "last updated" display
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Cleanup polling on unmount
    return () => {
      stopPolling();
      clearInterval(timeInterval);
    };
  }, []);

  // NEW: Start long polling
  const startPolling = () => {
    if (pollingInterval.current) return; // Already polling

    setIsPolling(true);
    pollingInterval.current = setInterval(() => {
      if (apiService.isAuthenticated()) {
        // Use the ref value to get current filter
        loadShiftsWithFilter(currentStatusFilter.current, true); // Silent reload during polling
      }
    }, POLLING_INTERVAL);
  };

  // NEW: Stop long polling
  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
    setIsPolling(false);
  };

  // Enhanced loadShifts with explicit filter parameter
  const loadShiftsWithFilter = async (
    filterStatus: typeof statusFilter,
    silent = false
  ) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError("");

    try {
      if (!apiService.isAuthenticated()) {
        setError("Authentication required. Please log in again.");
        return;
      }

      const filters = {} as Record<string, string>;
      if (filterStatus !== "all") {
        filters.status = filterStatus;
      }

      // Always use cache bypass for real-time data
      const response = await apiService.getShifts(filters, {
        bypassCache: true,
      });

      console.log("Shifts response:", response);
      if (response && Array.isArray(response)) {
        setShifts(response);
        setLastUpdated(new Date()); // Update last refresh time
      } else {
        setError("Invalid response format from server");
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        setError("Network error. Please check your connection and try again.");
      } else if (error instanceof ApiError) {
        setError(`API Error: ${error.message}`);
      } else {
        setError("An unexpected error occurred");
        console.error("Load shifts error:", error);
      }

      // Stop polling on error
      if (!silent) {
        stopPolling();
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  // Wrapper function for backward compatibility
  const loadShifts = async (silent = false) => {
    await loadShiftsWithFilter(statusFilter, silent);
  };

  const refreshData = () => {
    if (apiService.isAuthenticated()) {
      loadShiftsWithFilter(statusFilter);
    }
  };

  // Effect to reload data when status filter changes
  useEffect(() => {
    currentStatusFilter.current = statusFilter;
    loadShiftsWithFilter(statusFilter);

    // Restart polling with new filter if currently polling
    if (isPolling) {
      stopPolling();
      // Small delay to ensure clean restart
      setTimeout(() => {
        startPolling();
      }, 100);
    }
  }, [statusFilter]);

  // NEW: Format last updated time (now uses currentTime for live updates)
  const formatLastUpdated = (date: Date | null) => {
    if (!date) return "Never";
    const now = currentTime; // Use state instead of new Date()
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);

    if (diffSeconds < 5) {
      return "Just now";
    } else if (diffSeconds < 60) {
      return `${diffSeconds}s ago`;
    } else if (diffSeconds < 3600) {
      return `${Math.floor(diffSeconds / 60)}m ago`;
    } else {
      return date.toLocaleTimeString();
    }
  };

  const handleStatusChange = (shift: Shift) => {
    setSelectedShift(shift);
    setNewStatus(shift.status);
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedShift || !newStatus) return;

    setIsLoading(true);
    try {
      await apiService.updateShiftStatus(selectedShift.id, {
        new_status: newStatus,
      });

      // Refresh the shifts list
      await loadShifts();

      setShowStatusModal(false);
      setSelectedShift(null);
      setNewStatus("");
    } catch (error) {
      console.error("Failed to update shift status:", error);
      setError("Failed to update shift status");
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
      case "done":
        return "bg-blue-100 text-blue-800";
      case "locked":
        return "bg-red-100 text-red-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "Active";
      case "done":
        return "Completed";
      case "locked":
        return "Locked";
      default:
        return "Unknown";
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const calculateDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end.getTime() - start.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // NEW: Filter shifts by status locally as additional filtering
  const filteredShifts =
    statusFilter === "all"
      ? shifts
      : shifts.filter((shift) => shift.status === statusFilter);

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "locked", label: "Locked" },
  ];

  // NEW: Status filter options for dropdown
  const statusFilterOptions = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active Only" },
    { value: "locked", label: "Locked Only" },
    { value: "done", label: "Completed Only" },
  ];

  return (
    <Card padding="md" className="h-full overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Shift Management
        </h2>

        {/* Enhanced status and controls */}
        <div className="flex items-center gap-3 text-sm">
          {/* NEW: Last updated indicator */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div
              className={`w-2 h-2 rounded-full ${
                isPolling ? "bg-green-500 animate-pulse" : "bg-gray-400"
              }`}
            />
            <span>
              {isPolling ? "Live" : "Manual"} • Last updated:{" "}
              {formatLastUpdated(lastUpdated)}
            </span>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={refreshData}
            disabled={isLoading || !apiService.isAuthenticated()}
          >
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex justify-between items-center">
            <div className="text-sm text-yellow-800">{error}</div>
            {!isPolling && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setError("");
                  startPolling();
                }}
                className="text-xs"
              >
                Retry Live Updates
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div className="mb-4 flex gap-2 items-center">
        <label className="text-sm font-medium text-gray-700">
          Filter by Status:
        </label>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(
              e.target.value as "all" | "active" | "locked" | "done"
            )
          }
          className="p-2 border rounded"
          disabled={isLoading}
        >
          {statusFilterOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {statusFilter !== "all" && (
          <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded">
            Showing:{" "}
            {
              statusFilterOptions.find((opt) => opt.value === statusFilter)
                ?.label
            }
          </span>
        )}

        {/* NEW: Polling status indicator */}
        {isPolling && (
          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
            Auto-updating every {POLLING_INTERVAL / 1000}s
          </span>
        )}
      </div>

      {/* Enhanced Shifts List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-800">
            All Shifts ({filteredShifts.length})
            {statusFilter !== "all" && (
              <span className="text-sm text-gray-500 ml-2">
                (filtered from {shifts.length} total)
              </span>
            )}
          </h3>

          {/* NEW: Update indicator during polling */}
          {isPolling && !isLoading && (
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
              Checking for updates...
            </div>
          )}
        </div>

        <div className="border rounded overflow-auto max-h-[600px]">
          {filteredShifts.length > 0 ? (
            <div className="divide-y">
              {filteredShifts.map((shift) => (
                <div key={shift.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium text-gray-900">
                        {shift.pic_name}
                      </h4>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          shift.status
                        )}`}
                      >
                        {getStatusLabel(shift.status)}
                      </span>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleStatusChange(shift)}
                      disabled={isLoading}
                    >
                      Change Status
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Shift ID:</span>{" "}
                      {shift.id.slice(-8)}
                    </div>
                    <div>
                      <span className="font-medium">Duration:</span>{" "}
                      {calculateDuration(shift.start_time, shift.end_time)}
                    </div>
                    <div>
                      <span className="font-medium">Started:</span>{" "}
                      {formatDateTime(shift.start_time)}
                    </div>
                    {shift.end_time && (
                      <div>
                        <span className="font-medium">Ended:</span>{" "}
                        {formatDateTime(shift.end_time)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center">
              {statusFilter !== "all" ? (
                <div>
                  <p className="text-gray-500 mb-2">
                    No shifts found with status:{" "}
                    {
                      statusFilterOptions.find(
                        (opt) => opt.value === statusFilter
                      )?.label
                    }
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setStatusFilter("all")}
                    className="mr-2"
                  >
                    Show All Shifts
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500">No shifts found</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status Change Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => {
          if (!isLoading) {
            setShowStatusModal(false);
            setSelectedShift(null);
            setNewStatus("");
          }
        }}
        title="Change Shift Status"
        showCloseButton={!isLoading}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowStatusModal(false);
                setSelectedShift(null);
                setNewStatus("");
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateStatus}
              disabled={
                isLoading || !newStatus || newStatus === selectedShift?.status
              }
            >
              {isLoading ? "Updating..." : "Update Status"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {selectedShift && (
            <div className="p-3 bg-gray-50 rounded-md">
              <h4 className="font-medium text-gray-900 mb-2">
                {selectedShift.pic_name}
              </h4>
              <div className="text-sm text-gray-600">
                <div>Shift ID: {selectedShift.id.slice(-8)}</div>
                <div>
                  Current Status:{" "}
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      selectedShift.status
                    )}`}
                  >
                    {selectedShift.status}
                  </span>
                </div>
                <div>Started: {formatDateTime(selectedShift.start_time)}</div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Status
            </label>
            <select
              value={newStatus}
              onChange={(e) =>
                setNewStatus(e.target.value as "active" | "locked" | "done")
              }
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {newStatus === selectedShift?.status && (
            <div className="text-sm text-yellow-600">
              The selected status is the same as the current status.
            </div>
          )}
        </div>
      </Modal>
    </Card>
  );
};
